import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScheduledSources } from "@/components/ScheduledSources";
import { toast } from "sonner";
import { Globe, ExternalLink, Clock, CheckCircle2, CopyX, Newspaper, UploadCloud, FileText, Languages } from "lucide-react";

export default function Crawler() {
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [url, setUrl] = useState("");
  const [locality, setLocality] = useState("");
  const [ingest, setIngest] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [log, setLog] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [adapterPreview, setAdapterPreview] = useState(null);

  const loadLog = async () => {
    try {
      const { data } = await api.get("/crawl/log");
      setLog(data);
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    loadLog();
    api.get("/crawl/adapters").then(({ data }) => setAdapters(data.adapters || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!url || !url.startsWith("http")) { setAdapterPreview(null); return; }
    const t = setTimeout(() => {
      api.get("/crawl/adapters/preview", { params: { url } })
        .then(({ data }) => setAdapterPreview(data))
        .catch(() => setAdapterPreview(null));
    }, 400);
    return () => clearTimeout(t);
  }, [url]);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/crawl", {
        url: url.trim(),
        locality: locality.trim(),
        ingest,
      });
      setResult(data);
      if (data.adapter) {
        // Multi-ad newspaper adapter path.
        if (data.ingested > 0) {
          toast.success(`Ingested ${data.ingested} ad(s) via ${data.adapter}`);
          setUrl("");
          loadLog();
        } else if (data.duplicate_count > 0) {
          toast.warning(`All ${data.duplicate_count} ad(s) on this page were duplicates`);
        } else {
          toast.warning("Adapter matched but found no ingestable ads");
        }
      } else if (data.ingested) {
        toast.success("Page crawled and ingested");
        setUrl("");
        loadLog();
      } else if (data.duplicate) {
        toast.warning("Duplicate content — this page matches an existing advertisement");
      } else {
        toast.success("Page fetched (preview only)");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Crawl failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  // ---- E-paper PDF upload -------------------------------------------------
  const fileRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfLocality, setPdfLocality] = useState("Bangalore");
  const [pdfSourceName, setPdfSourceName] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfResult, setPdfResult] = useState(null);

  const runPdf = async (save) => {
    if (!pdfFile) {
      toast.error("Choose a PDF first");
      return;
    }
    setPdfBusy(true);
    try {
      const form = new FormData();
      form.append("file", pdfFile);
      form.append("locality", pdfLocality || "");
      form.append("source_name", pdfSourceName || pdfFile.name);
      form.append("save", save ? "true" : "false");
      const { data } = await api.post("/crawl/upload-epaper", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfResult(data);
      if (save) {
        toast.success(`Ingested ${data.ingested} ad(s) · ${data.duplicates} duplicate(s)`);
        if (data.ingested > 0) loadLog();
      } else {
        toast.success(`Parsed ${data.candidate_ads} candidate ad(s) (preview)`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF processing failed");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="crawler-page">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-blue-700">
          Data ingestion
        </div>
        <h1 className="font-display font-bold tracking-tight text-3xl">
          Public source crawler
        </h1>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          Ingest a single public URL. The crawler respects robots.txt,
          extracts title, text, and images, computes perceptual image
          hashes for near-duplicate detection, and creates an
          advertisement record for review.
        </p>
      </header>

      <section className="border border-slate-200 bg-white rounded-md p-5 space-y-3 max-w-3xl">
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-mono">
              Public URL
            </label>
            <Input
              data-testid="crawl-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://public-classifieds.example.com/listing/123"
              className="mt-1"
              required
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-mono">
                Locality (optional)
              </label>
              <Input
                data-testid="crawl-locality-input"
                value={locality}
                onChange={(e) => setLocality(e.target.value)}
                placeholder="MG Road, Koramangala…"
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="crawl-ingest-toggle"
                  checked={ingest}
                  onChange={(e) => setIngest(e.target.checked)}
                  className="accent-blue-600"
                />
                Save as advertisement
              </label>
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="crawl-submit"
            className="bg-slate-900 hover:bg-slate-800 gap-2"
          >
            <Globe className="h-4 w-4" />
            {busy ? "Crawling…" : "Crawl URL"}
          </Button>
          {adapterPreview && (
            <div
              data-testid="adapter-preview"
              className={`text-[11px] font-mono px-2 py-1.5 rounded-md border inline-flex items-center gap-2 ${
                adapterPreview.matched
                  ? "bg-blue-50 border-blue-200 text-blue-800"
                  : "bg-slate-50 border-slate-200 text-slate-500"
              }`}
            >
              <Newspaper className="h-3.5 w-3.5" />
              {adapterPreview.matched
                ? `Newspaper adapter: ${adapterPreview.adapter} (multi-ad listing)`
                : "No newspaper adapter matched — will be crawled as a single ad"}
            </div>
          )}
        </form>

        {result && (
          <div className="border-t border-slate-100 pt-4 space-y-2" data-testid="crawl-result">
            {result.adapter ? (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">
                    {result.adapter} · {result.ingested} ingested · {result.duplicate_count || 0} duplicates · {result.failures || 0} failed
                  </span>
                </div>
                {result.category_matched && (
                  <div className="text-xs text-slate-500 font-mono">
                    Matched category: {result.category_matched}
                  </div>
                )}
                {(result.ad_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {result.ad_ids.slice(0, 12).map((id) => (
                      <Link key={id} to={`/ads/${id}`} className="text-[11px] font-mono px-2 py-0.5 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100">
                        {id.slice(0, 8)}
                      </Link>
                    ))}
                    {result.ad_ids.length > 12 && (
                      <span className="text-[11px] text-slate-400 font-mono">+ {result.ad_ids.length - 12} more</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">{result.page?.title}</span>
                </div>
                {result.duplicate && (
                  <div data-testid="crawl-duplicate-notice" className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                    <CopyX className="h-4 w-4 shrink-0" />
                    <span>
                      Identical content already ingested — no new ad created.{" "}
                      <Link className="underline font-medium" to={`/ads/${result.duplicate_of}`}>Open existing ad</Link>
                    </span>
                  </div>
                )}
                <div className="text-xs text-slate-500 font-mono">
                  {result.computed_hashes?.length || 0} image hash(es) computed
                  {result.ingested && result.ad_id && (
                    <>
                      {" · "}
                      <Link className="text-blue-700 hover:underline" to={`/ads/${result.ad_id}`}>
                        Open new ad
                      </Link>
                    </>
                  )}
                </div>
                {result.page?.text && (
                  <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 max-h-64 overflow-auto whitespace-pre-wrap">
                    {result.page.text}
                  </pre>
                )}
              </>
            )}
          </div>
        )}
      </section>

      <section
        className="border border-slate-200 bg-white rounded-md p-5 space-y-3 max-w-3xl"
        data-testid="epaper-section"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
              <UploadCloud className="h-5 w-5 text-purple-600" /> E-paper PDF upload
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Drop a daily e-paper page (English or Kannada). Text PDFs are read
              directly; scanned pages are OCR'd with Tesseract (eng+kan). Each
              detected classified is split on phone-number boundaries and shown
              for preview before saving.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_180px_150px] gap-3 items-end">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-mono">PDF file</label>
            <Input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              data-testid="epaper-file"
              onChange={(e) => { setPdfFile(e.target.files?.[0] || null); setPdfResult(null); }}
              className="mt-1 file:mr-3 file:py-1 file:px-2 file:border-0 file:text-xs file:bg-slate-100 file:rounded"
              disabled={readOnly}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-mono">Locality</label>
            <Input
              data-testid="epaper-locality"
              value={pdfLocality}
              onChange={(e) => setPdfLocality(e.target.value)}
              className="mt-1"
              disabled={readOnly}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-mono">Source label</label>
            <Input
              data-testid="epaper-source-name"
              value={pdfSourceName}
              onChange={(e) => setPdfSourceName(e.target.value)}
              placeholder="e.g. Deccan Herald"
              className="mt-1"
              disabled={readOnly}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="epaper-preview-btn"
            onClick={() => runPdf(false)}
            disabled={pdfBusy || !pdfFile || readOnly}
            className="gap-1.5 border-slate-300"
          >
            <FileText className="h-4 w-4" />
            {pdfBusy ? "Parsing…" : "Preview split"}
          </Button>
          <Button
            type="button"
            data-testid="epaper-save-btn"
            onClick={() => runPdf(true)}
            disabled={pdfBusy || !pdfFile || readOnly}
            className="bg-purple-600 hover:bg-purple-700 gap-1.5"
          >
            <UploadCloud className="h-4 w-4" />
            {pdfBusy ? "Ingesting…" : "Ingest all"}
          </Button>
        </div>

        {pdfResult && (
          <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="epaper-result">
            <div className="text-xs text-slate-600 font-mono flex flex-wrap gap-3">
              <span>Pages: {pdfResult.pages}</span>
              <span className={pdfResult.ocr_used ? "text-purple-700" : ""}>
                {pdfResult.ocr_used ? (
                  <><Languages className="h-3 w-3 inline" /> OCR (eng+kan)</>
                ) : "Text layer"}
              </span>
              <span>Candidates: {pdfResult.candidate_ads}</span>
              {pdfResult.saved && (
                <>
                  <span className="text-emerald-700">Ingested: {pdfResult.ingested}</span>
                  <span className="text-amber-700">Duplicates: {pdfResult.duplicates}</span>
                </>
              )}
            </div>
            {pdfResult.message && <div className="text-xs text-slate-500">{pdfResult.message}</div>}
            <div className="space-y-1.5">
              {(pdfResult.ads_preview || []).map((p, i) => (
                <div key={p.id || `${p.title}-${i}`} className="border border-slate-200 rounded-md p-2.5" data-testid={`epaper-preview-${i}`}>
                  <div className="text-sm font-medium truncate">{p.title}</div>
                  <div className="text-xs text-slate-600 line-clamp-2 mt-0.5 whitespace-pre-wrap">{p.text}</div>
                </div>
              ))}
              {(pdfResult.ads_preview || []).length === 0 && (
                <div className="text-xs text-slate-400">No ad candidates parsed from this PDF.</div>
              )}
            </div>
          </div>
        )}
      </section>

      {adapters.length > 0 && (
        <section
          className="border border-slate-200 bg-white rounded-md p-5 max-w-3xl"
          data-testid="supported-newspapers"
        >
          <h2 className="font-display font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-blue-600" /> Supported newspaper classifieds
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Paste any classified LISTING page from these sites and the crawler will split it into
            individual ads (relevant categories only). Deep article pages fall back to the generic single-ad path.
          </p>
          <ul className="space-y-1.5">
            {adapters.map((a) => (
              <li key={a.name} className="text-sm flex items-center gap-2 flex-wrap">
                <span className="font-medium">{a.name.replace(/_/g, " ")}</span>
                <span className="text-[10px] font-mono text-slate-500">
                  {a.hosts.join(", ")}
                </span>
                {a.requires_js && (
                  <span
                    className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                    data-testid={`adapter-js-badge-${a.name}`}
                    title="This site needs headless-browser rendering (Playwright)."
                  >
                    JS render
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <ScheduledSources readOnly={readOnly} />

      <section className="border border-slate-200 bg-white rounded-md p-5 max-w-3xl">
        <h2 className="font-display font-bold text-lg tracking-tight mb-3">
          Recent crawls
        </h2>        <ul className="divide-y divide-slate-100">
          {log.map((row) => (
            <li key={row.id} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to={`/ads/${row.ad_id}`}
                  className="text-sm truncate block hover:underline"
                  data-testid={`crawl-log-${row.id}`}
                >
                  {row.title}
                </Link>
                <a
                  href={row.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-slate-500 font-mono truncate flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" /> {row.url}
                </a>
              </div>
              <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1 whitespace-nowrap">
                <Clock className="h-3 w-3" />
                {(row.crawled_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </li>
          ))}
          {log.length === 0 && (
            <li className="text-xs text-slate-400 py-2">No crawls yet.</li>
          )}
        </ul>
      </section>
    </div>
  );
}
